import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import type { ApiResponse } from '@/types';

interface PageViewStats {
  page_id: number;
  page_slug: string;
  page_title: string;
  view_count: number;
  unique_visitors: number;
}

interface DailyViewStats {
  date: string;
  view_count: number;
  unique_visitors: number;
}

interface OverallStats {
  totalViews: number;
  uniqueVisitors: number;
  avgViewsPerPage: number;
  topPages: PageViewStats[];
}

interface QueryParams {
  days?: number;
  limit?: number;
}

export function useAnalyticsStats(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['analyticsStats', params],
    queryFn: async (): Promise<OverallStats> => {
      const searchParams = new URLSearchParams();
      if (params.days) searchParams.set('days', String(params.days));
      const response = await api.get<ApiResponse<OverallStats>>(`/analytics/stats?${searchParams}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch analytics');
      }
      return response.data.data;
    },
  });
}

export function useTopPages(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['topPages', params],
    queryFn: async (): Promise<PageViewStats[]> => {
      const searchParams = new URLSearchParams();
      if (params.days) searchParams.set('days', String(params.days));
      if (params.limit) searchParams.set('limit', String(params.limit));
      const response = await api.get<ApiResponse<PageViewStats[]>>(`/analytics/top-pages?${searchParams}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch top pages');
      }
      return response.data.data;
    },
  });
}

export function useDailyViewStats(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['dailyViewStats', params],
    queryFn: async (): Promise<DailyViewStats[]> => {
      const searchParams = new URLSearchParams();
      if (params.days) searchParams.set('days', String(params.days));
      const response = await api.get<ApiResponse<DailyViewStats[]>>(`/analytics/daily?${searchParams}`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'Failed to fetch daily stats');
      }
      return response.data.data;
    },
  });
}

export type { PageViewStats, DailyViewStats, OverallStats };
